import React, { useEffect } from "react";
import { Form, Input, Button, Card, Spin } from "antd";
import { Plus, Trash2, Save, Video } from "lucide-react";
import UploadImage from "../../components/UploadImage";
import { useGetCmsConfig, useSaveCmsConfig } from "../../../hooks/api/cms/hooks";
import { useMessageApi } from "../../../provider/AntdNotification";

const VideoGalleryCMS: React.FC = () => {
  const [form] = Form.useForm();
  const messageApi = useMessageApi();
  const { data: configData, isLoading } = useGetCmsConfig<any>("VIDEO_GALLERY");
  const saveMutation = useSaveCmsConfig<any>({
    page: "VIDEO_GALLERY",
    onSuccess: () => {
      messageApi.success("Video Gallery configuration saved successfully.");
    },
    onError: (err: any) => messageApi.error(err?.message ?? "Failed to save configuration"),
  });

  useEffect(() => {
    if (configData?.data) {
      const cfg = configData.data;
      form.setFieldsValue({
        pageTitle: cfg.pageTitle || "Video Gallery",
        pageDescription: cfg.pageDescription || "",
        videos: cfg.videos || [],
      });
    }
  }, [configData, form]);

  const onFinish = (values: any) => {
    const formattedValues = {
      ...values,
      videos: values.videos?.map((video: any) => ({
        ...video,
        thumbnail:
          Array.isArray(video.thumbnail) && video.thumbnail.length > 0
            ? video.thumbnail[0]
            : video.thumbnail,
        videoUrl:
          Array.isArray(video.videoUrl) && video.videoUrl.length > 0
            ? video.videoUrl[0]
            : video.videoUrl,
      })),
    };
    saveMutation.mutate(formattedValues);
  };

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 pb-12">
      <div className="w-full">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-800 flex items-center gap-2">
              <Video className="text-primary" size={24} /> Video Gallery / Making of Videos CMS
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage the page title, description, and videos list for the website.
            </p>
          </div>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        className="flex flex-col gap-6"
      >
        {/* PAGE HEADER SECTION */}
        <Card
          title={
            <span className="font-semibold text-slate-800">
              Page Header Section
            </span>
          }
        >
          <Form.Item name="pageTitle" label="Page Title" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. Video Gallery or Making of Videos" />
          </Form.Item>

          <Form.Item name="pageDescription" label="Page Description / Subtitle">
            <Input.TextArea
              rows={3}
              placeholder="e.g. Discover museum-quality contemporary sculpture..."
            />
          </Form.Item>
        </Card>

        {/* VIDEOS LIST */}
        <Card
          title={
            <div className="flex justify-between items-center w-full">
              <span className="font-semibold text-slate-800">Videos List</span>
            </div>
          }
        >
          <Form.List name="videos">
            {(fields, { add, remove }) => (
              <div className="flex flex-col gap-6 mt-2">
                {fields.map(({ key, name, ...restField }) => (
                  <div
                    key={key}
                    className="flex flex-col md:flex-row gap-6 p-5 bg-slate-50 border border-slate-200 rounded-xl relative"
                  >
                    <div className="absolute top-3 right-3 z-10">
                      <Button
                        type="text"
                        danger
                        icon={<Trash2 size={16} />}
                        onClick={() => remove(name)}
                      />
                    </div>

                    <div className="w-full md:w-1/3 flex flex-col gap-4">
                      <Form.Item
                        {...restField}
                        name={[name, "thumbnail"]}
                        label="Video Poster / Thumbnail Image"
                        className="mb-0"
                      >
                        <UploadImage count={1} listType="picture-card" acceptType="image" />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, "videoUrl"]}
                        label="Upload Video File (MP4, WebM)"
                        className="mb-0"
                        rules={[{ required: true, message: "Please upload a video file" }]}
                      >
                        <UploadImage count={1} listType="picture-card" acceptType="video" accept="video/*" />
                      </Form.Item>
                    </div>

                    <div className="w-full md:w-2/3 flex flex-col gap-4">
                      <Form.Item
                        {...restField}
                        name={[name, "title"]}
                        label="Video Title"
                        className="mb-0"
                        rules={[{ required: true, message: "Required" }]}
                      >
                        <Input placeholder="e.g. Lost Wax Bronze Casting" />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, "description"]}
                        label="Video Description"
                        className="mb-0"
                      >
                        <Input.TextArea
                          rows={4}
                          placeholder="Describe the techniques, craftsmanship, and story behind this video..."
                        />
                      </Form.Item>
                    </div>
                  </div>
                ))}

                <Button
                  type="dashed"
                  onClick={() => add()}
                  icon={<Plus size={16} />}
                  className="flex items-center justify-center gap-2 h-12"
                >
                  Add New Video
                </Button>
              </div>
            )}
          </Form.List>
        </Card>
      </Form>

      {/* Sticky Footer Save Bar */}
      <div className="sticky bottom-4 bg-white/95 backdrop-blur border border-slate-200 py-3 px-6 rounded-xl z-40 flex items-center justify-end shadow-lg">
        <Button
          type="primary"
          size="large"
          icon={<Save size={18} />}
          loading={saveMutation.isPending}
          onClick={() => form.submit()}
          className="!bg-primary flex items-center gap-2 px-8 shadow-md"
        >
          Save Configuration
        </Button>
      </div>
    </div>
  );
};

export default VideoGalleryCMS;
